import { ChangeEvent, useEffect, useState } from "react";
import { Autocomplete } from "@mui/material";
import { useAppSelector } from "@/store/hooks";
import { selectEndpointList } from "@/store/ses/sesEndpointSlice";
import { useDebounce, useTranslate } from "@/hooks";
import { OPERATION_SET_VARIABLE_LIST, TOperationSetVariableExtended } from "@/constants/robotConfigLists";
import { IActionSetVariable } from "@/types/ses/sesItemConfigTypes";
import { RenderOption } from "@/ui";
import { CustomMenuItem, CustomTextField } from "@/ui";
import { ISaveHandlerProps, ISetVariableProps } from "./SetVariable.props";

const SetVariable = ({ action, idx, channel, isDisabled, handleSaveActionInStore }: ISetVariableProps): JSX.Element => {
	const [inputVariable, setInputVariable] = useState<string>(action.variable); // переменная
	const [inputOperation, setInputOperation] = useState<TOperationSetVariableExtended>(action.operation === "set" && action.value === null ? "del" : action.operation); // операция
	const [inputValue, setInputValue] = useState<string | null>(action.value); // значение

	const endpointList = useAppSelector(selectEndpointList); // список КТ

	const translate = useTranslate(); // hook для перевода текста
	const debouncedInputVariable = useDebounce(inputVariable, 1000); // задержка обработки переменной
	const debouncedInputValue = useDebounce(inputValue, 1000); // задержка обработки значения

	// следим за переменной
	useEffect(() => {
		if (debouncedInputVariable !== action.variable) {
			setVariableActionDataChangeHandler({ variable: debouncedInputVariable });
		}
	}, [debouncedInputVariable]);

	// следим за значением
	useEffect(() => {
		if (debouncedInputValue !== action.value) {
			setVariableActionDataChangeHandler({ value: debouncedInputValue });
		}
	}, [debouncedInputValue]);

	// обработчик изменения переменной
	const handleVariableChange = (_e: React.SyntheticEvent<Element, Event>, value: string | null): void => {
		const formattedValue = value || "";
		setInputVariable(formattedValue);
		action.variable !== formattedValue && setVariableActionDataChangeHandler({ variable: formattedValue });
	};

	// обработчик изменения операции
	const handleOperationChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
		const value = e.target.value as TOperationSetVariableExtended;
		// значение "del" условное и равно значению "set"
		if (value === "del") {
			setInputOperation(value);
			// вынужденно, проблема с перерисовкой
			setInputValue(""); // 1 сброс поля значения
			setTimeout(() => {
				setInputValue(null); // 2 сброс поля значения
				setVariableActionDataChangeHandler({ operation: value, value: null });
			});
		} else {
			setInputOperation(value);
			inputValue === null && setInputValue("");
			setVariableActionDataChangeHandler({ operation: value, value: inputValue === null ? "" : undefined });
		}
	};

	// обработчик изменения значения
	const handleValueChange = (_e: React.SyntheticEvent<Element, Event>, value: string | null): void => {
		const formattedValue = value || "";
		setInputValue(formattedValue);
		action.value !== formattedValue && setVariableActionDataChangeHandler({ value: formattedValue });
	};

	// обработчик изменения данных действия "Установить переменную" (setVariable)
	const setVariableActionDataChangeHandler = ({ variable, operation, value }: Partial<ISaveHandlerProps>): void => {
		const data: { channel: string; idx: number; action: Partial<IActionSetVariable> } = {
			channel,
			idx,
			action: {
				variable: variable ?? inputVariable,
				operation: operation === "del" || inputOperation === "del" ? "set" : operation ?? inputOperation,
				value: value !== undefined ? value : inputValue,
			},
		};
		handleSaveActionInStore(data);
	};

	return (
		<>
			{/* переменная */}
			<Autocomplete
				fullWidth
				freeSolo
				autoHighlight
				options={endpointList.variableNames}
				value={inputVariable}
				onChange={handleVariableChange}
				disabled={isDisabled}
				title={inputVariable}
				renderInput={(params) => (
					<CustomTextField {...params} label={translate("input_variable")} error={!inputVariable} onChange={(e) => setInputVariable(e.target.value)} />
				)}
				renderOption={(props, option) => {
					return (
						<RenderOption {...props} key={option}>
							{option}
						</RenderOption>
					);
				}}
			/>

			{/* операция */}
			<CustomTextField select value={inputOperation} onChange={handleOperationChange} disabled={isDisabled} sx={{ flexShrink: 0 }}>
				{OPERATION_SET_VARIABLE_LIST.map(({ operation, translation }) => (
					<CustomMenuItem key={operation} value={operation}>
						{translation}
					</CustomMenuItem>
				))}
			</CustomTextField>

			{/* значение */}
			<Autocomplete
				fullWidth
				freeSolo
				autoHighlight
				options={endpointList.variableValues}
				value={inputValue}
				onChange={handleValueChange}
				disabled={isDisabled || inputValue === null}
				title={inputValue || ""}
				renderInput={(params) => (
					<CustomTextField
						{...params}
						label={translate("input_value")}
						error={inputValue === "" && inputVariable !== "del"}
						onChange={(e) => setInputValue(e.target.value)}
					/>
				)}
				renderOption={(props, option) => {
					return (
						<RenderOption {...props} key={option}>
							{option}
						</RenderOption>
					);
				}}
			/>
		</>
	);
};

export default SetVariable;
